import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'
import urlParser from 'js-video-url-parser'

import { HOST_NAME } from '@data/config/url'

import { Metadata } from '../Metadata'
import { buildBreadcrumbList, HOME_BREADCRUMB } from '../buildBreadcrumbList'

import { PresentationMetadata_presentation$key } from './__generated__/PresentationMetadata_presentation.graphql'

export const PresentationMetadata: FC<{
  presentation: PresentationMetadata_presentation$key
  title?: string
  description?: string
}> = ({
  presentation,
  title: overridenTitle,
  description: overridenDescription,
}) => {
  const {
    createdAt,
    description,
    presenter,
    presenterFirstName,
    presenterLastName,
    title: presentationTitle,
    videoSourceUrl,
    prettyUrl,
    updatedAt,
  } = useFragment(
    graphql`
      fragment PresentationMetadata_presentation on Presentation {
        title
        description

        presenterFirstName
        presenterLastName

        videoSourceUrl
        prettyUrl

        createdAt
        updatedAt

        presenter {
          firstName
          lastName
          slugId
          primaryPhoto {
            seoUrl: transformUrl(
              transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
            )
          }
        }
      }
    `,
    presentation
  )

  const photoUrl = useMemo(() => {
    if (videoSourceUrl) {
      const videoInfo = urlParser.parse(videoSourceUrl)

      if (videoInfo?.provider === 'youtube') {
        const photoUrl = urlParser.create({
          format: 'longImage',
          params: { imageQuality: 'maxresdefault' },
          videoInfo,
        })

        if (photoUrl) {
          return photoUrl
        }
      }
    }

    return `${HOST_NAME}/social/presentation/${prettyUrl}/card.png?${updatedAt}`
  }, [videoSourceUrl, prettyUrl, updatedAt])

  const name = presenter
    ? `${presenter.firstName} ${presenter.lastName}`
    : `${presenterFirstName} ${presenterLastName}`

  const title = overridenTitle || `${presentationTitle} by ${name}`

  const canonicalUrl = `${HOST_NAME}/presentations/${prettyUrl}`

  const contentDescription =
    overridenDescription || description || `${presentationTitle} by ${name}`

  const structuredData = useMemo(() => {
    const videoName = presentationTitle || `Presentation by ${name}`
    const videoDescription = contentDescription || videoName

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: presentationTitle,
      url: canonicalUrl,
      description: contentDescription,
      author: {
        '@type': 'Person',
        name,
        ...(presenter?.slugId
          ? { url: `${HOST_NAME}/${presenter.slugId}` }
          : {}),
        ...(presenter?.primaryPhoto?.seoUrl
          ? { image: presenter.primaryPhoto.seoUrl }
          : {}),
      },
      datePublished: createdAt,
      dateModified: updatedAt,
      ...(photoUrl ? { thumbnailUrl: photoUrl } : {}),
      ...(videoSourceUrl
        ? {
            video: {
              '@type': 'VideoObject',
              contentUrl: videoSourceUrl,
              name: videoName,
              description: videoDescription,
              thumbnailUrl: photoUrl,
              uploadDate: createdAt || updatedAt,
            },
          }
        : {}),
    })
  }, [
    presentationTitle,
    canonicalUrl,
    contentDescription,
    name,
    presenter,
    photoUrl,
    videoSourceUrl,
    createdAt,
    updatedAt,
    prettyUrl,
  ])

  useScript({
    type: 'application/ld+json',
    text: structuredData,
  })

  const breadcrumbData = useMemo(
    () =>
      buildBreadcrumbList([
        HOME_BREADCRUMB,
        { name: 'Presentations', url: `${HOST_NAME}/presentations` },
        { name: presentationTitle, url: canonicalUrl },
      ]),
    [presentationTitle, canonicalUrl]
  )

  useScript({ type: 'application/ld+json', text: breadcrumbData })

  return (
    <Metadata
      canonicalUrl={canonicalUrl}
      description={contentDescription}
      imageTransformParams='NONE'
      imageWidth={2400}
      imageHeight={1200}
      ogType='article'
      photoUrl={photoUrl}
      title={title}
    />
  )
}
